import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { combinedOdds, entryResult } from '../../lib/tips';

const EMPTY_MATCH = { home: '', away: '', league: '', pick: '', odd: '', ft: '', result: 'WIN' };
const DATE_RE = /^\d{2}\.\d{2}\.\d{4}$/;

function MatchFields({ idx, m, onChange, showIndex }) {
  const set = (k, v) => onChange({ ...m, [k]: v });
  return (
    <div className="rounded-2xl border border-line p-4 bg-mist/50" data-testid={`entry-form-match-${idx}`}>
      {showIndex && <div className="font-mono text-xs text-royal mb-3">MATCH 0{idx + 1}</div>}
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label>Home team</Label><Input data-testid={`match-${idx}-home`} value={m.home} onChange={e => set('home', e.target.value)} className="mt-1 h-10" /></div>
        <div><Label>Away team</Label><Input data-testid={`match-${idx}-away`} value={m.away} onChange={e => set('away', e.target.value)} className="mt-1 h-10" /></div>
        <div className="sm:col-span-2"><Label>League (optional)</Label><Input data-testid={`match-${idx}-league`} value={m.league || ''} onChange={e => set('league', e.target.value)} placeholder="Italy Serie A" className="mt-1 h-10" /></div>
        <div><Label>Pick</Label><Input data-testid={`match-${idx}-pick`} value={m.pick} onChange={e => set('pick', e.target.value)} placeholder="1 · Over 2.5 · Lazio -1.5 AH" className="mt-1 h-10" /></div>
        <div><Label>Odd</Label><Input data-testid={`match-${idx}-odd`} type="number" step="0.01" min="1.01" value={m.odd} onChange={e => set('odd', e.target.value)} className="mt-1 h-10 font-mono" /></div>
        <div><Label>FT score (optional)</Label><Input data-testid={`match-${idx}-ft`} value={m.ft} onChange={e => set('ft', e.target.value)} placeholder="2:1" className="mt-1 h-10 font-mono" /></div>
        <div>
          <Label>Result</Label>
          <Select value={m.result} onValueChange={v => set('result', v)}>
            <SelectTrigger data-testid={`match-${idx}-result`} className="mt-1 h-10"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="WIN">WIN</SelectItem>
              <SelectItem value="LOST">LOST</SelectItem>
              <SelectItem value="PENDING">PENDING</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

export default function EntryForm({ tipster, initial, onSave, onCancel }) {
  const legs = tipster.format === 'double' ? 2 : 1;
  const [date, setDate] = useState(initial?.date || '');
  const [noPick, setNoPick] = useState(initial ? initial.matches.length === 0 : false);
  const [note, setNote] = useState(initial?.note || 'No pick released — the betting offer did not meet our criteria.');
  const [matches, setMatches] = useState(
    initial?.matches?.length ? initial.matches.map(m => ({ ...EMPTY_MATCH, ...m })) : Array.from({ length: legs }, () => ({ ...EMPTY_MATCH }))
  );
  const [saving, setSaving] = useState(false);

  const preview = { matches: noPick ? [] : matches.map(m => ({ ...m, odd: Number(m.odd) || 1 })) };
  const combined = combinedOdds(preview.matches);

  const submit = async (e) => {
    e.preventDefault();
    if (!DATE_RE.test(date)) return alert('Date must be in DD.MM.YYYY format.');
    if (!noPick && matches.some(m => !m.home || !m.away || !m.pick || !(Number(m.odd) > 1))) return alert('Each match needs home, away, pick and an odd above 1.00.');
    setSaving(true);
    try {
      await onSave({
        tipster_id: tipster.id, date,
        note: noPick ? note : null,
        matches: noPick ? [] : matches.map(m => ({ ...m, odd: Number(m.odd), league: m.league || null }))
      });
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="entry-form">
      <div className="grid sm:grid-cols-[1fr_auto] gap-4 items-end">
        <div><Label htmlFor="entry-date">Date (DD.MM.YYYY)</Label><Input id="entry-date" data-testid="entry-form-date" value={date} onChange={e => setDate(e.target.value)} placeholder="02.11.2026" className="mt-1 h-11 font-mono" /></div>
        <label className="flex items-center gap-3 h-11 px-4 rounded-xl border border-line bg-white text-sm font-medium cursor-pointer"><Switch checked={noPick} onCheckedChange={setNoPick} data-testid="entry-form-no-pick" /> No pick this day</label>
      </div>

      {noPick ? (
        <div><Label>Note shown in archive</Label><Input data-testid="entry-form-note" value={note} onChange={e => setNote(e.target.value)} className="mt-1 h-11" /></div>
      ) : (
        <>
          {matches.map((m, i) => <MatchFields key={i} idx={i} m={m} showIndex={legs > 1} onChange={nm => setMatches(ms => ms.map((x, j) => (j === i ? nm : x)))} />)}
          <div className="rounded-2xl bg-navy text-white p-4 flex items-center justify-between">
            <div className="text-xs text-slate-300">{legs > 1 ? 'Combined odds (auto) · Day result' : 'Odds · Result'}</div>
            <div className="font-mono font-bold text-lg" data-testid="entry-form-combined">{combined ? combined.toFixed(2) : '—'} · {entryResult(preview)}</div>
          </div>
        </>
      )}

      <div className="flex gap-2 justify-end">
        <Button type="button" variant="outline" onClick={onCancel} className="rounded-full" data-testid="entry-form-cancel">Cancel</Button>
        <Button type="submit" disabled={saving} className="bg-royal hover:bg-royal-dark text-white rounded-full px-6" data-testid="entry-form-save">{saving ? 'Saving…' : 'Save entry'}</Button>
      </div>
    </form>
  );
}
